import { useState, useCallback, useRef, useEffect, useMemo } from "react";
import {
  Page,
  Layout,
  Card,
  Text,
  BlockStack,
  InlineGrid,
  Badge,
  DataTable,
  Button,
  Icon,
  Box,
  Select,
  InlineStack,
  Spinner,
  Popover,
  TextField,
  useBreakpoints,
  OptionList,
  Scrollable,
  DatePicker,
  Tooltip as ToolTip,
  LegacyCard,
} from "@shopify/polaris";
import * as XLSX from 'xlsx';
import {
  ExportIcon,
  InfoIcon,
  CalendarIcon,
  ArrowRightIcon,
} from "@shopify/polaris-icons";
// import { LineChart } from "@mui/x-charts/LineChart";
import Chart from 'react-apexcharts';
import useFreshworksWidget from "./useFreshworksWidget";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  closestCorners,
  MouseSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  rectSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { EditIcon, ResetIcon } from "@shopify/polaris-icons";
import ReviewManager, { useReviewTrigger } from "./ReviewManager";

// Helper function for formatting dates
function formatDateToYearMonthDayDateString(date) {
  const year = String(date.getFullYear());
  let month = String(date.getMonth() + 1);
  let day = String(date.getDate());
  if (month.length < 2) month = "0" + month;
  if (day.length < 2) day = "0" + day;
  return [year, month, day].join("-");
}

// Custom header for Polaris DatePicker (multiMonth)
function MonthHeader({ month, year, onPrev, onNext }) {
  const left = new Date(year, month, 1);
  const right = new Date(year, month + 1, 1);

  const leftLabel = left.toLocaleString("en-US", { month: "long" });
  const rightLabel = right.toLocaleString("en-US", { month: "long" });

  return (
    <div className="dp-header">
      <span className="dp-label">
        {leftLabel} — {rightLabel}
      </span>
    </div>
  );
}

function DateRangePicker({ initialDateRange, onChange, disabled = false }) {
  const { mdDown, lgUp } = useBreakpoints();
  const shouldShowMultiMonth = lgUp;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  const ranges = [
    {
      title: "Last 7 days",
      alias: "last7days",
      period: {
        since: new Date(
          new Date(new Date().setDate(today.getDate() - 7)).setHours(0, 0, 0, 0)
        ),
        until: today,
      },
    },
    {
      title: "Last 30 days",
      alias: "last30days",
      period: {
        since: new Date(
          new Date(new Date().setDate(today.getDate() - 30)).setHours(
            0,
            0,
            0,
            0
          )
        ),
        until: today,
      },
    },
    {
      title: "Last 90 days",
      alias: "last90days",
      period: {
        since: new Date(
          new Date(new Date().setDate(today.getDate() - 90)).setHours(
            0,
            0,
            0,
            0
          )
        ),
        until: today,
      },
    },
  ];
  const [popoverActive, setPopoverActive] = useState(false);
  const [activeDateRange, setActiveDateRange] = useState(
    initialDateRange || ranges[1]
  ); // Default to Last 30 days
  const [inputValues, setInputValues] = useState({});
  const [{ month, year }, setDate] = useState({
    month: activeDateRange.period.since.getMonth(),
    year: activeDateRange.period.since.getFullYear(),
  });

  const datePickerRef = useRef(null);
  const VALID_YYYY_MM_DD_DATE_REGEX = /^\d{4}-\d{1,2}-\d{1,2}/;

  // # Add this useEffect inside DateRangePicker component, after the state definitions
  useEffect(() => {
    if (popoverActive) {
      const timer = setTimeout(() => {
        const titles = document.querySelectorAll(".Polaris-DatePicker__Title");
        titles.forEach((title) => {
          const text = title.textContent.trim();
          // Assuming format "Month Year", remove last 5 chars (space + 4-digit year)
          if (/\s\d{4}$/.test(text)) {
            title.textContent = text.slice(0, -5);
          }
        });
      }, 100); // Small delay to ensure DOM is updated

      return () => clearTimeout(timer);
    }
  }, [popoverActive, month, year]); // Re-run on navigation

  function isDate(date) {
    return !isNaN(new Date(date).getDate());
  }

  function isValidYearMonthDayDateString(date) {
    return VALID_YYYY_MM_DD_DATE_REGEX.test(date) && isDate(date);
  }

  function isValidDate(date) {
    return date.length === 10 && isValidYearMonthDayDateString(date);
  }

  function parseYearMonthDayDateString(input) {
    const [year, month, day] = input.split("-");
    return new Date(Number(year), Number(month) - 1, Number(day));
  }

  // Update formatDate to use the global function directly
  function formatDate(date) {
    return formatDateToYearMonthDayDateString(date);
  }

  function nodeContainsDescendant(rootNode, descendant) {
    if (rootNode === descendant) return true;
    let parent = descendant.parentNode;
    while (parent != null) {
      if (parent === rootNode) return true;
      parent = parent.parentNode;
    }
    return false;
  }

  function isNodeWithinPopover(node) {
    return datePickerRef?.current
      ? nodeContainsDescendant(datePickerRef.current, node)
      : false;
  }

  function handleStartInputValueChange(value) {
    setInputValues((prevState) => ({ ...prevState, since: value }));
    if (isValidDate(value)) {
      const newSince = parseYearMonthDayDateString(value);
      setActiveDateRange((prevState) => {
        const newPeriod =
          prevState.period && newSince <= prevState.period.until
            ? { since: newSince, until: prevState.period.until }
            : { since: newSince, until: newSince };
        return { ...prevState, period: newPeriod };
      });
    }
  }

  function handleEndInputValueChange(value) {
    setInputValues((prevState) => ({ ...prevState, until: value }));
    if (isValidDate(value)) {
      const newUntil = parseYearMonthDayDateString(value);
      setActiveDateRange((prevState) => {
        const newPeriod =
          prevState.period && newUntil >= prevState.period.since
            ? { since: prevState.period.since, until: newUntil }
            : { since: newUntil, until: newUntil };
        return { ...prevState, period: newPeriod };
      });
    }
  }

  function handleInputBlur({ relatedTarget }) {
    const isRelatedTargetWithinPopover =
      relatedTarget != null && isNodeWithinPopover(relatedTarget);

    // Check if the related target is part of the DatePicker calendar
    const isDatePickerElement = relatedTarget && (
      relatedTarget.closest('.Polaris-DatePicker') ||
      relatedTarget.closest('.Polaris-Popover__Pane') ||
      relatedTarget.closest('[role="grid"]') // DatePicker uses role="grid"
    );

    if (isRelatedTargetWithinPopover || isDatePickerElement) return;

    setPopoverActive(false);
  }

  function handleCalendarChange({ start, end }) {
    const newDateRange = ranges.find(
      (range) =>
        range.period.since.valueOf() === start.valueOf() &&
        range.period.until.valueOf() === end.valueOf()
    ) || {
      alias: "custom",
      title: "Custom Range",
      period: { since: start, until: end },
    };
    setActiveDateRange(newDateRange);
  }

  function apply() {
    if (onChange) onChange(activeDateRange.period); // Propagate to parent
    setPopoverActive(false);
  }

  function cancel() {
    setPopoverActive(false);
  }

  useEffect(() => {
    if (activeDateRange) {
      setInputValues({
        since: formatDate(activeDateRange.period.since),
        until: formatDate(activeDateRange.period.until),
      });
      function monthDiff(referenceDate, newDate) {
        return (
          newDate.month -
          referenceDate.month +
          12 * (referenceDate.year - newDate.year)
        );
      }
      const monthDifference = monthDiff(
        { year, month },
        {
          year: activeDateRange.period.until.getFullYear(),
          month: activeDateRange.period.until.getMonth(),
        }
      );
      if (monthDifference > 1 || monthDifference < 0) {
        setDate({
          month: activeDateRange.period.until.getMonth(),
          year: activeDateRange.period.until.getFullYear(),
        });
      }
    }
  }, [activeDateRange]);

  const buttonValue =
    activeDateRange.alias === "custom"
      ? `${activeDateRange.period.since.toDateString()} - ${activeDateRange.period.until.toDateString()}`
      : activeDateRange.title;

  return (
    <Popover
      active={popoverActive}
      autofocusTarget="none"
      preferredAlignment="left"
      preferredPosition="below"
      fluidContent
      sectioned={false}
      fullHeight
      activator={
        <Button
          size="slim"
          icon={CalendarIcon}
          onClick={() => setPopoverActive(!popoverActive)}
        >
          {buttonValue}
        </Button>
      }
      onClose={() => setPopoverActive(false)}
    >
      <Popover.Pane fixed>
        <InlineGrid
          columns={{ xs: "1fr", mdDown: "1fr", md: "max-content max-content" }}
          gap={0}
          ref={datePickerRef}
        >
          <Box
            maxWidth={mdDown ? "516px" : "212px"}
            width={mdDown ? "100%" : "212px"}
            padding={{ xs: 500, md: 0 }}
            paddingBlockEnd={{ xs: 100, md: 0 }}
          >
            {mdDown ? (
              <Select
                label="dateRangeLabel"
                labelHidden
                onChange={(value) => {
                  const result = ranges.find(
                    ({ title, alias }) => title === value || alias === value
                  );
                  setActiveDateRange(result);
                }}
                value={activeDateRange?.title || activeDateRange?.alias || ""}
                options={ranges.map(({ alias, title }) => title || alias)}
              />
            ) : (
              <Scrollable style={{ height: "334px" }}>
                <OptionList
                  options={ranges.map((range) => ({
                    value: range.alias,
                    label: range.title,
                  }))}
                  selected={[activeDateRange.alias]}
                  onChange={(value) => {
                    setActiveDateRange(
                      ranges.find((range) => range.alias === value[0])
                    );
                  }}
                />
              </Scrollable>
            )}
          </Box>
          <Box padding={{ xs: 500 }} maxWidth={mdDown ? "320px" : "516px"}>
            <BlockStack gap="400">
              <InlineStack gap="200">
                <div style={{ flexGrow: 1 }}>
                  <TextField
                    role="combobox"
                    label={"Since"}
                    labelHidden
                    prefix={<Icon source={CalendarIcon} />}
                    value={inputValues.since}
                    onChange={handleStartInputValueChange}
                    onBlur={handleInputBlur}
                    autoComplete="off"
                  />
                </div>
                <Icon source={ArrowRightIcon} />
                <div style={{ flexGrow: 1 }}>
                  <TextField
                    role="combobox"
                    label={"Until"}
                    labelHidden
                    prefix={<Icon source={CalendarIcon} />}
                    value={inputValues.until}
                    onChange={handleEndInputValueChange}
                    onBlur={handleInputBlur}
                    autoComplete="off"
                  />
                </div>
              </InlineStack>
              <div className="dp-with-header">
                <MonthHeader
                  month={month}
                  year={year}
                  onPrev={() => {
                    const prev = new Date(year, month - 1, 1);
                    setDate({
                      month: prev.getMonth(),
                      year: prev.getFullYear(),
                    });
                  }}
                  onNext={() => {
                    const next = new Date(year, month + 1, 1);
                    setDate({
                      month: next.getMonth(),
                      year: next.getFullYear(),
                    });
                  }}
                />
                <DatePicker
                  selected={{
                    start: activeDateRange.period.since,
                    end: activeDateRange.period.until,
                  }}
                  onChange={handleCalendarChange}
                  allowRange
                  month={month}
                  year={year}
                  multiMonth
                  onMonthChange={(m, y) => setDate({ month: m, year: y })}
                />
              </div>
            </BlockStack>
          </Box>
        </InlineGrid>
      </Popover.Pane>
      <Popover.Pane fixed>
        <Popover.Section>
          <InlineStack align="end">
            <div style={{ display: "flex", flexDirection: "row", gap: "8px" }}>
              <Button onClick={cancel}>Cancel</Button>
              <Button variant="primary" onClick={apply}>
                Apply
              </Button>
            </div>
          </InlineStack>
        </Popover.Section>
      </Popover.Pane>
    </Popover>
  );
}

function SortableCard({
  id,
  editMode,
  children,
  className = "",
  style: customStyle,
  draggingHeight,
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id, disabled: !editMode });

  const style = {
    transform: transform
      ? `translate3d(${transform.x}px, ${transform.y}px, 0)`
      : undefined,
    transition: isDragging
      ? "none"
      : "transform 0.2s ease, box-shadow 0.2s ease",
    zIndex: isDragging ? 100 : "auto",
    background: "white",
    borderRadius: "8px",
    position: "relative",
    opacity: isDragging ? 0.5 : 1,
    height:
      isDragging && draggingHeight[id] ? `${draggingHeight[id]}px` : undefined,
    cursor: editMode ? (isDragging ? "grabbing" : "grab") : "default",
    ...customStyle,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`sortable-card ${editMode ? "is-edit" : ""} ${className}`}
      data-dragging={isDragging}
      data-id={id}
      {...attributes}
      {...listeners}
    >
      {children}
    </div>
  );
}

function Dashboard({ shopOrigin, apiKey }) {
  const navigate = useNavigate();
  const { smDown, mdDown } = useBreakpoints();
  // NEW: Metrics State
  const [metricsData, setMetricsData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dateRange, setDateRange] = useState("30");
  const [shopDomain, setShopDomain] = useState(null);
  const [articlesList, setArticlesList] = useState([]);
  const [selectedArticle, setSelectedArticle] = useState("all");
  const [blogsList, setBlogsList] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [currentDatePeriod, setCurrentDatePeriod] = useState(null);
  const { openWidget } = useFreshworksWidget(206000000367, true);
  // Add state for insights
  const [insights, setInsights] = useState({
    performance_insights: [],
    content_optimization: [],
    strategic_actions: [],
  });
  const [insightsLoading, setInsightsLoading] = useState(false);
  const [sessionLoading, setSessionLoading] = useState(false);
  const [topPostsBySessions, setTopPostsBySessions] = useState([]);
  const [topPostsSessionsCount, setTopPostsSessionsCount] = useState("5");
  // const [dateRangeSessions, setDateRangeSessions] = useState({
  //   since: new Date(new Date().setDate(new Date().getDate() - 30)),
  //   until: new Date(),
  // });

  // Top 5 Engagements State
  const [engagementLoading, setEngagementLoading] = useState(false);
  const [topEngagementData, setTopEngagementData] = useState([]);
  const [topEngagementCount, setTopEngagementCount] = useState("5");
  // const [dateRangeEngagement, setDateRangeEngagement] = useState({
  //   since: new Date(new Date().setDate(new Date().getDate() - 30)),
  //   until: new Date(),
  // });

  // Top Clicked Products State
  const [topProductsLoading, setTopProductsLoading] = useState(false);
  const [topProductsData, setTopProductsData] = useState([]);
  const [topProductsCount, setTopProductsCount] = useState(5);
  // const [dateRangeProducts, setDateRangeProducts] = useState({
  //   since: new Date(new Date().setDate(new Date().getDate() - 30)),
  //   until: new Date(),
  // });

  // Add state for chart data
  const [chartData, setChartData] = useState({ views: [], engagement: [] });
  const [chartLoading, setChartLoading] = useState(false);
  // const [chartDateRange, setChartDateRange] = useState({
  //   since: new Date(new Date().setDate(new Date().getDate() - 30)),
  //   until: new Date(),
  // });

  // Global Date range state
  const [globalDateRange, setGlobalDateRange] = useState({
    since: new Date(new Date().setDate(new Date().getDate() - 30)),
    until: new Date(),
  });

  // Add state for the new Top Articles table
  const [topArticlesData, setTopArticlesData] = useState([]);
  const [topArticlesLoading, setTopArticlesLoading] = useState(false);
  const [topArticlesCount, setTopArticlesCount] = useState(5);
  const [dateRangeArticles, setDateRangeArticles] = useState({
    since: new Date(new Date().setDate(new Date().getDate() - 30)),
    until: new Date(),
  });


  // Add this state in your Dashboard component
  const [isExporting, setIsExporting] = useState(false);


  // Add these new state variables for popover active states
  const [categoryPopoverActive, setCategoryPopoverActive] = useState(false);
  const [articlePopoverActive, setArticlePopoverActive] = useState(false);
  const [topPostsPopoverActive, setTopPostsPopoverActive] = useState(false);
  const [topEngagementPopoverActive, setTopEngagementPopoverActive] =
    useState(false);
  const [topProductsPopoverActive, setTopProductsPopoverActive] =
    useState(false);

  // ============================================
  // ADD THIS: Blog ID to Category Mapping
  // ============================================
  const [blogIdToCategoryMap, setBlogIdToCategoryMap] = useState({});


  // ============================================
  // Layout State
  // ============================================




  // Helper function to get label from value
  const getCategoryLabel = (value) => {
    const item = blogsList.find((b) => b.value === value);
    return item ? item.label : "All Categories";
  };

  const getArticleLabel = (value) => {
    const item = articlesList.find((a) => a.value === value);
    return item ? item.label : "All Articles";
  };

  const getTopPostsLabel = (value) => {
    const v = String(value);
    const options = [
      { label: "Top 5", value: "5" },
      { label: "Top 10", value: "10" },
      { label: "All", value: "All" },
    ];
    const item = options.find((o) => o.value === v);
    return item ? item.label : "Top 5";
  };


  const getTopEngagementLabel = (value) => {
    const v = String(value);
    const options = [
      { label: "Top 5", value: "5" },
      { label: "Top 10", value: "10" },
      { label: "All", value: "All" },
    ];
    const item = options.find((o) => o.value === v);
    return item ? item.label : "Top 5";
  };

  const getTopProductsLabel = (value) => {
    const v = String(value);
    const options = [
      { label: "Top 5", value: "5" },
      { label: "Top 10", value: "10" },
      { label: "All", value: "All" },
    ];
    const item = options.find((o) => o.value === v);
    return item ? item.label : "Top 5";
  };

  const [activeTab, setActiveTab] = useState("Views");

  // Define ranges for DateRangePicker
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const ranges = [
    {
      title: "Last 7 days",
      alias: "last7days",
      period: {
        since: new Date(
          new Date(new Date().setDate(today.getDate() - 7)).setHours(0, 0, 0, 0)
        ),
        until: today,
      },
    },
    {
      title: "Last 30 days",
      alias: "last30days",
      period: {
        since: new Date(
          new Date(new Date().setDate(today.getDate() - 30)).setHours(
            0,
            0,
            0,
            0
          )
        ),
        until: today,
      },
    },
    {
      title: "Last 90 days",
      alias: "last90days",
      period: {
        since: new Date(
          new Date(new Date().setDate(today.getDate() - 90)).setHours(
            0,
            0,
            0,
            0
          )
        ),
        until: today,
      },
    },
  ];

  // Add this handler function inside your Dashboard component
  const handleExportData = useCallback(async () => {
    setIsExporting(true);
    try {
      const params = new URLSearchParams({
        shop: shopDomain,
      });

      // Add date range filters
      if (currentDatePeriod) {
        params.append('since', formatDateToYearMonthDayDateString(currentDatePeriod.since));
        params.append('until', formatDateToYearMonthDayDateString(currentDatePeriod.until));
      } else {
        params.append('days', dateRange);
      }

      // Add article filter
      if (selectedArticle && selectedArticle !== 'all') {
        params.append('article_id', selectedArticle);
      }

      // Add category filter
      if (selectedCategory && selectedCategory !== 'all') {
        const categoryName = blogIdToCategoryMap[selectedCategory];
        if (categoryName) {
          params.append('category', categoryName);
        }
      }

      // Call backend API to get export data
      const response = await axios.get(
        `/analytics/export-data/?${params.toString()}`,
        { responseType: 'blob' }
      );

      // Create blob and trigger download
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `analytics_export_${new Date().getTime()}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);

      // Show success notification
      // You can add a toast notification here if needed
    } catch (error) {
      console.error('Error exporting data:', error);
      // Show error notification
    } finally {
      setIsExporting(false);
    }
  }, [shopDomain, dateRange, currentDatePeriod, selectedArticle, selectedCategory, blogIdToCategoryMap]);

  // Get initial DateRangePicker config from dateRange
  const getInitialDateRange = useCallback((daysStr) => {
    const days = parseInt(daysStr, 10);
    if (days === 7) return ranges[0];
    if (days === 30) return ranges[1];
    if (days === 90) return ranges[2];
    return ranges[1]; // Default to 30 days
  }, []);

  // Add state for loading and errors
  const [loading, setLoading] = useState(false);
  // const [error, setError] = useState(null);

  // ============================================
  // CORRECTED: fetchArticlesAndBlogs useEffect
  // ============================================
  useEffect(() => {
    if (!shopDomain) return;

    const fetchArticlesAndBlogs = async () => {
      setLoading(true);
      setError(null);

      try {
        const params = new URLSearchParams({ shop: shopDomain });

        // Use axios like your other API calls for consistency
        const response = await axios.get(`/analytics/get-articles-blogs/?${params.toString()}`);

        // console.log('API Response:', response.data);

        if (response.data.status === 'success') {
          // Create mapping from blog ID to category name
          const categoryMap = {};
          if (response.data.blogs && Array.isArray(response.data.blogs)) {
            response.data.blogs.forEach(blog => {
              categoryMap[blog.blog_id] = blog.title;
              // console.log(`Mapped ${blog.blog_id} -> ${blog.title}`);
            });
          }
          // console.log('Final Blog ID to Category Map:', categoryMap);
          setBlogIdToCategoryMap(categoryMap);

          // Format Articles WITH category information
          if (response.data.articles && Array.isArray(response.data.articles)) {
            const formattedArticles = response.data.articles.map(a => ({
              label: a.article_title || 'Untitled',
              value: a.article_id.toString(),
              category: a.category || '', // Store category with article
              blog_id: a.blog_id || '' // Store blog_id with article
            }));

            setArticlesList([
              { label: 'All Articles', value: 'all' },
              ...formattedArticles
            ]);
            // console.log('Articles loaded:', formattedArticles.length);
          }

          // Format Blogs (Categories)
          if (response.data.blogs && Array.isArray(response.data.blogs) && response.data.blogs.length > 0) {
            const formattedBlogs = response.data.blogs.map(b => ({
              label: b.title,
              value: b.blog_id.toString()
            }));

            setBlogsList([
              { label: 'All Categories', value: 'all' },
              ...formattedBlogs
            ]);
            // console.log('Categories loaded:', formattedBlogs.length);
          }
        } else {
          throw new Error(response.data.message || 'An unknown error occurred');
        }
      } catch (err) {
        console.error('Error fetching articles and blogs:', err);

        // More detailed error logging
        if (err.response) {
          // Server responded with an error status
          console.error('Response data:', err.response.data);
          console.error('Response status:', err.response.status);
          setError(`Server error: ${err.response.status}`);
        } else if (err.request) {
          // Request was made but no response received
          console.error('No response received:', err.request);
          setError('No response from server');
        } else {
          // Something else happened
          setError(err.message);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchArticlesAndBlogs();
  }, [shopDomain]);




  // ADD: Compute filtered articles based on selected category
  const filteredArticlesList = useMemo(() => {
    if (!selectedCategory || selectedCategory === 'all') {
      return articlesList;
    }

    // Get the category name from the blog ID
    const categoryName = blogIdToCategoryMap[selectedCategory];

    // console.log('Filtering articles for category:', categoryName);

    if (!categoryName) {
      return articlesList;
    }

    // Filter articles - keep "All Articles" option and matching articles
    const filtered = articlesList.filter(article => {
      if (article.value === 'all') return true; // Keep "All Articles" option

      // Case-insensitive comparison
      return article.category &&
        article.category.toLowerCase() === categoryName.toLowerCase();
    });

    // console.log('Filtered articles count:', filtered.length - 1); // -1 for "All Articles"

    return filtered;
  }, [articlesList, selectedCategory, blogIdToCategoryMap]);

  // Get shop domain from URL params
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const shop = urlParams.get("shop");
    if (shop) {
      setShopDomain(shop);
    }
  }, []);

  // Fetch Dashboard Metrics with Filters
  const fetchDashboardMetrics = useCallback(async () => {
    if (!shopDomain) return;

    setIsLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams({
        shop: shopDomain,
      });

      // Check if we have a custom date range with period
      if (currentDatePeriod) {
        const sinceDate = formatDateToYearMonthDayDateString(
          currentDatePeriod.since
        );
        const untilDate = formatDateToYearMonthDayDateString(
          currentDatePeriod.until
        );

        params.append("since", sinceDate);
        params.append("until", untilDate);
      } else {
        params.append("days", dateRange);
      }

      // Add article filter if not "all"
      if (selectedArticle && selectedArticle !== "all") {
        params.append("article_id", selectedArticle);
      }

      // ✅ FIX: Convert blog_id to category name before sending
      if (selectedCategory && selectedCategory !== "all") {
        const categoryName = blogIdToCategoryMap[selectedCategory];
        if (categoryName) {
          params.append("category", categoryName);  // ✅ Changed from blog_id to category
          // console.log("✅ Dashboard metrics - Filtering by category name:", categoryName);
        }
      }

      const response = await fetch(
        `/analytics/dashboard-metrics/?${params.toString()}`
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();

      // Transform the response data dynamically
      const transformedData = [
        {
          title: "Total Blog Views",
          value: data.total_blog_views?.value || "0",
          change: data.total_blog_views?.change || "0",
          subtext: `vs last ${dateRange} days`,
          trend: data.total_blog_views?.trend || "neutral",
          dotColor: "#4ade80",
        },
        {
          title: "Clicks to Products",
          value: data.clicks_to_products?.value || "0",
          change: data.clicks_to_products?.change || "0",
          subtext: "from blog articles",
          trend: data.clicks_to_products?.trend || "neutral",
          dotColor: "#60a5fa",
        },
        {
          title: "Top Product Clicks",
          value: data.top_product_clicks?.value || "No Data",
          subvalue: data.top_product_clicks?.subvalue || "",
          change: data.top_product_clicks?.change || "0",
          subtext: `vs last ${dateRange} days`,
          trend: data.top_product_clicks?.trend || "neutral",
          dotColor: "#a78bfa",
        },
        {
          title: "Avg Session Duration",
          value: data.avg_session_duration?.value || "0m 0s",
          change: data.avg_session_duration?.change || "0",
          subtext: "per session",
          trend: data.avg_session_duration?.trend || "neutral",
          dotColor: "#22d3ee",
        },
      ];

      setMetricsData(transformedData);
    } catch (err) {
      console.error("Error fetching dashboard metrics:", err);
      setError(err.message || "Failed to load metrics");
    } finally {
      setIsLoading(false);
    }
  }, [
    shopDomain,
    dateRange,
    selectedArticle,
    selectedCategory,
    currentDatePeriod,
    // blogIdToCategoryMap,  // ✅ Add this dependency
  ]);

  // Component for rendering trend with color coding
  const TrendCell = ({ value }) => {
    const sign = value > 0 ? "+" : "";
    const formattedValue =
      value % 1 === 0 ? Math.round(value) : value.toFixed(1);
    const tone = value > 0 ? "success" : value < 0 ? "critical" : "subdued";

    return (
      <Text as="span" tone={tone} fontWeight="medium" variant="bodySm">
        {sign}
        {formattedValue}%
      </Text>
    );
  };

  // ============================================
  // 3. FIXED fetchTopPostsBySessions - Add dependencies
  // ============================================
  const fetchTopPostsBySessions = useCallback(
    async (limit) => {
      setSessionLoading(true);
      try {
        const params = new URLSearchParams({
          shop: shopOrigin,
          since: formatDateToYearMonthDayDateString(globalDateRange.since),
          until: formatDateToYearMonthDayDateString(globalDateRange.until),
          limit: limit,
        });

        // ✅ CRITICAL: Add these filters
        if (selectedArticle && selectedArticle !== "all") {
          params.append("article_id", selectedArticle);
          // console.log("Filtering by article:", selectedArticle);
        }
        // ✅ FIXED: Convert blog ID to category name
        if (selectedCategory && selectedCategory !== "all") {
          const categoryName = blogIdToCategoryMap[selectedCategory];
          if (categoryName) {
            params.append("category", categoryName);
            // console.log("✅ Filtering by category name:", categoryName);
          }
        }

        const response = await axios.get(
          `/analytics/top-posts/?${params.toString()}`
        );

        // ✅ FIX: Convert blog ID to category name for URL params
        const rows = response.data.filter(post => post.title && post.title.trim().toLowerCase() !== 'untitled').map((post) => {
          const articleParams = new URLSearchParams({
            shop: encodeURIComponent(shopOrigin || ""),
            since: formatDateToYearMonthDayDateString(globalDateRange.since),
            until: formatDateToYearMonthDayDateString(globalDateRange.until),
          });

          // Add category NAME if a category is selected
          if (selectedCategory && selectedCategory !== "all") {
            const categoryName = blogIdToCategoryMap[selectedCategory];
            if (categoryName) {
              articleParams.append("category", categoryName);
            }
          }

          return [
            <Link
              className="Polaris-Text--root Polaris-Text--bodySm"
              to={`/article/article_id=${post.article_id}?${articleParams.toString()}`}
              style={{
                color: "#005BD3",
                textDecoration: "none",
                cursor: "pointer",
              }}
            >
              {post.title || "Untitled"}
            </Link>,
            <InlineStack gap="150" blockAlign="center" align="end">
              <ToolTip content="Total number of views for this article">
                <Text as="span" fontWeight="medium">
                  {post.views.toString()}
                </Text>
              </ToolTip>
            </InlineStack>,
            <TrendCell value={post.trend} />,
          ];
        });
        setTopPostsBySessions(rows);
      } catch (error) {
        console.error("Error fetching top posts by sessions:", error);
        setTopPostsBySessions([]);
      } finally {
        setSessionLoading(false);
      }
    },
    [shopOrigin, selectedArticle, selectedCategory, globalDateRange, blogIdToCategoryMap]
  );

  // ============================================
  // 4. FIXED fetchTopEngagement - Add dependencies
  // ============================================
  const fetchTopEngagement = useCallback(
    async (limit) => {
      setEngagementLoading(true);
      try {
        const params = new URLSearchParams({
          shop: shopOrigin,
          since: formatDateToYearMonthDayDateString(globalDateRange.since),
          until: formatDateToYearMonthDayDateString(globalDateRange.until),
          limit: limit,
        });

        // ✅ CRITICAL: Add these filters
        if (selectedArticle && selectedArticle !== "all") {
          params.append("article_id", selectedArticle);
          // console.log("Engagement filtering by article:", selectedArticle);
        }
        // ✅ FIXED: Convert blog ID to category name
        if (selectedCategory && selectedCategory !== "all") {
          const categoryName = blogIdToCategoryMap[selectedCategory];
          if (categoryName) {
            params.append("category", categoryName);
            // console.log(
            //   "✅ Engagement - Filtering by category name:",
            //   categoryName
            // );
          }
        }

        const response = await axios.get(
          `/analytics/engagement/?${params.toString()}`
        );

        // ✅ FIX: Convert blog ID to category name for URL params
        const rows = response.data.per_article.filter(article => article.title && article.title.trim().toLowerCase() !== 'untitled').map((article) => {
          const articleParams = new URLSearchParams({
            shop: encodeURIComponent(shopOrigin || ""),
            since: formatDateToYearMonthDayDateString(globalDateRange.since),
            until: formatDateToYearMonthDayDateString(globalDateRange.until),
          });

          // Add category NAME if a category is selected
          if (selectedCategory && selectedCategory !== "all") {
            const categoryName = blogIdToCategoryMap[selectedCategory];
            if (categoryName) {
              articleParams.append("category", categoryName);
            }
          }

          return [
            <Link
              className="Polaris-Text--root Polaris-Text--bodySm"
              to={`/article/article_id=${article.article_id}?${articleParams.toString()}`}
              style={{
                color: "#005BD3",
                textDecoration: "none",
                cursor: "pointer",
              }}
            >
              {article.title || "Untitled"}
            </Link>,
            <InlineStack gap="150" blockAlign="center" align="end">
              <ToolTip content="Percentage of session time spent engaging with this article">
                <Text as="span" fontWeight="medium">
                  {article.engagement_percentage.toFixed(1)}%
                </Text>
              </ToolTip>
            </InlineStack>,
            <TrendCell value={article.trend} />,
          ];
        });
        setTopEngagementData(rows);
      } catch (error) {
        console.error("Error fetching engagement data:", error);
        setTopEngagementData([]);
      } finally {
        setEngagementLoading(false);
      }
    },
    [shopOrigin, selectedArticle, selectedCategory, globalDateRange]
  );

  // ============================================
  // 5. FIXED fetchTopProducts - Add dependencies
  // ============================================
  const fetchTopProducts = useCallback(
    async (limit) => {
      setTopProductsLoading(true);
      try {
        const params = new URLSearchParams({
          shop: shopOrigin,
          since: formatDateToYearMonthDayDateString(globalDateRange.since),
          until: formatDateToYearMonthDayDateString(globalDateRange.until),
          limit: limit,
        });

        // ✅ CRITICAL: Add these filters
        if (selectedArticle && selectedArticle !== "all") {
          params.append("article_id", selectedArticle);
          console.log("Products filtering by article:", selectedArticle);
        }
        // ✅ FIXED: Convert blog ID to category name
        if (selectedCategory && selectedCategory !== "all") {
          const categoryName = blogIdToCategoryMap[selectedCategory];
          if (categoryName) {
            params.append("category", categoryName);
          }
        }

        const response = await axios.get(
          `/analytics/top-clicked-products/?${params.toString()}`
        );

        console.log("URLS: ", `/analytics/top-clicked-products/?${params.toString()}`)
        console.log("response: ", response);

        const rows = response.data.map((product) => [
          <Text as="span" variant="bodySm">
            {product.title || "Untitled Product"}
          </Text>,
          <InlineStack gap="150" blockAlign="center" align="end">
            <ToolTip content="Number of times this product was clicked from blog articles">
              <Text as="span" fontWeight="medium">
                {product.clicks.toString()}
              </Text>
            </ToolTip>
          </InlineStack>,
          <TrendCell value={product.trend} />,
        ]);
        setTopProductsData(rows);
      } catch (error) {
        console.error("Error fetching top products:", error);
        setTopProductsData([]);
      } finally {
        setTopProductsLoading(false);
      }
    },
    [shopOrigin, selectedArticle, selectedCategory, globalDateRange]
  );

  // ============================================
  // 6. FIXED fetchChartData - Add dependencies
  // ============================================
  const fetchChartData = useCallback(
    async (type) => {
      setChartLoading(true);
      try {
        const params = new URLSearchParams({
          shop: shopOrigin,
          since: formatDateToYearMonthDayDateString(globalDateRange.since),
          until: formatDateToYearMonthDayDateString(globalDateRange.until),
          type: type,
        });

        // ✅ CRITICAL: Add these filters
        if (selectedArticle && selectedArticle !== "all") {
          params.append("article_id", selectedArticle);
          // console.log("Chart filtering by article:", selectedArticle);
        }

        // ✅ FIXED: Convert blog ID to category name
        if (selectedCategory && selectedCategory !== "all") {
          const categoryName = blogIdToCategoryMap[selectedCategory];
          if (categoryName) {
            params.append("category", categoryName);
          }
        }

        const response = await axios.get(
          `/analytics/charts-by-session/?${params.toString()}`
        );

        return response.data;
      } catch (error) {
        console.error("Error fetching chart data:", error);
        return [];
      } finally {
        setChartLoading(false);
      }
    },
    [shopOrigin, selectedArticle, selectedCategory, globalDateRange]
  );

  // ============================================
  // 11. FIXED useEffect for chartovertime
  // ============================================
  useEffect(() => {
    if (!shopOrigin) return;

    const loadChartData = async () => {
      // console.log(
      //   "Refetching chart - selectedArticle:",
      //   selectedArticle,
      //   "selectedCategory:",
      //   selectedCategory,
      //   "type:",
      //   activeTab
      // );
      const data = await fetchChartData(activeTab.toLowerCase());

      setChartData((prevData) => ({
        ...prevData,
        [activeTab.toLowerCase()]: data,
      }));
    };

    loadChartData();
  }, [
    shopOrigin,
    globalDateRange,
    activeTab,
    fetchChartData,
    selectedArticle, // ✅ CRITICAL: Add this
    selectedCategory, // ✅ CRITICAL: Add this
    // blogIdToCategoryMap, // Add this
  ]);

  // ============================================
  // 7. FIXED fetchTopArticles - Add dependencies
  // ============================================
  const fetchTopArticles = useCallback(
    async (limit) => {
      setTopArticlesLoading(true);
      try {
        const params = new URLSearchParams({
          shop: shopOrigin,
          since: formatDateToYearMonthDayDateString(globalDateRange.since),
          until: formatDateToYearMonthDayDateString(globalDateRange.until),
          limit: limit,
        });

        if (selectedArticle && selectedArticle !== "all") {
          params.append("article_id", selectedArticle);
          // console.log("Top articles filtering by article:", selectedArticle);
        }
        if (selectedCategory && selectedCategory !== "all") {
          const categoryName = blogIdToCategoryMap[selectedCategory];
          if (categoryName) {
            params.append("category", categoryName);
          }
        }

        const response = await axios.get(
          `/analytics/top-articles-data/?${params.toString()}`
        );

        const rows = response.data.filter(article => article.title && article.title.trim().toLowerCase() !== 'untitled').map((article) => {
          const articleParams = new URLSearchParams({
            shop: encodeURIComponent(shopOrigin || ""),
            since: formatDateToYearMonthDayDateString(globalDateRange.since),
            until: formatDateToYearMonthDayDateString(globalDateRange.until),
          });

          if (selectedCategory && selectedCategory !== "all") {
            const categoryName = blogIdToCategoryMap[selectedCategory];
            if (categoryName) {
              articleParams.append("category", categoryName);
            }
          }

          return [
            <Link
              className="Polaris-Text--root Polaris-Text--bodySm"
              to={`/article/article_id=${article.id}?${articleParams.toString()}`}
              style={{ color: "#005BD3", textDecoration: "none", cursor: "pointer" }}
            >
              {article.title || "Untitled Article"}
            </Link>,
            article.views,
            `${article.engagement.toFixed(1)}%`,
            article.conversions,
          ];
        });
        setTopArticlesData(rows);
        // setTopArticlesData([]);
      } catch (error) {
        console.error("Error fetching top articles data:", error);
        setTopArticlesData([]);
      } finally {
        setTopArticlesLoading(false);
      }
    },
    [shopOrigin, selectedArticle, selectedCategory, globalDateRange, blogIdToCategoryMap]
  );



  // ============================================
  // 8. FIXED useEffect for topSessions
  // ============================================
  useEffect(() => {
    if (!shopOrigin) return;
    // console.log(
    //   "Refetching topSessions - selectedArticle:",
    //   selectedArticle,
    //   "selectedCategory:",
    //   selectedCategory
    // );
    fetchTopPostsBySessions(topPostsSessionsCount);
  }, [
    shopOrigin,
    globalDateRange,
    fetchTopPostsBySessions,
    topPostsSessionsCount,
    selectedArticle, // ✅ CRITICAL: Add this
    selectedCategory, // ✅ CRITICAL: Add this
    // blogIdToCategoryMap, // Add this
  ]);

  // ============================================
  // 9. FIXED useEffect for topEngagement
  // ============================================
  useEffect(() => {
    if (!shopOrigin) return;
    // console.log(
    //   "Refetching topEngagement - selectedArticle:",
    //   selectedArticle,
    //   "selectedCategory:",
    //   selectedCategory
    // );
    fetchTopEngagement(topEngagementCount);
  }, [
    shopOrigin,
    globalDateRange,
    fetchTopEngagement,
    topEngagementCount,
    selectedArticle, // ✅ CRITICAL: Add this
    selectedCategory, // ✅ CRITICAL: Add this
    // blogIdToCategoryMap, // Add this
  ]);

  // ============================================
  // 10. FIXED useEffect for topProducts
  // ============================================
  useEffect(() => {
    if (!shopOrigin) return;
    // console.log(
    //   "Refetching topProducts - selectedArticle:",
    //   selectedArticle,
    //   "selectedCategory:",
    //   selectedCategory
    // );
    fetchTopProducts(topProductsCount);
  }, [
    shopOrigin,
    globalDateRange,
    fetchTopProducts,
    topProductsCount,
    selectedArticle, // ✅ CRITICAL: Add this
    selectedCategory, // ✅ CRITICAL: Add this
    // blogIdToCategoryMap, // Add this
  ]);

  // ============================================
  // 12. FIXED useEffect for topArticles
  // ============================================
  useEffect(() => {
    if (!shopOrigin) return;
    // console.log(
    //   "Refetching topArticles - selectedArticle:",
    //   selectedArticle,
    //   "selectedCategory:",
    //   selectedCategory
    // );
    fetchTopArticles(topArticlesCount);
  }, [
    shopOrigin,
    globalDateRange,
    topArticlesCount,
    fetchTopArticles,
    selectedArticle, // ✅ CRITICAL: Add this
    selectedCategory, // ✅ CRITICAL: Add this
    // blogIdToCategoryMap, // Add this
  ]);

  // Fetch dashboard metrics
  useEffect(() => {
    fetchDashboardMetrics();
  }, [fetchDashboardMetrics]);

  // --- UPDATE DATE RANGE HANDLER ---
  const handleDateRangeChange = useCallback((selected) => {
    // ✅ Handle both cases: full object or just period
    const period = selected.period || selected;

    // Calculate days from the period
    const calculateDays = (period) => {
      const diffTime = Math.abs(period.until - period.since);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return diffDays;
    };

    const calculatedDays = calculateDays(period);

    setDateRange(calculatedDays);
    setCurrentDatePeriod(period);
    setGlobalDateRange({
      since: period.since,
      until: period.until,
    });
  }, []);


  // Handle article filter change
  const handleArticleChange = useCallback((value) => {
    setSelectedArticle(value);
    // console.log("Article filter changed to:", value); // Debug log
  }, []);

  // Handle category filter change
  const handleCategoryChange = useCallback((value) => {
    setSelectedCategory(value);
    // console.log("Category filter changed to:", value); // Debug log
  }, []);


  // ADD THIS: Auto-select category when article is selected
  useEffect(() => {
    if (!selectedArticle || selectedArticle === 'all') {
      // If no article is selected, don't auto-select category
      setSelectedCategory('all');
      return;
    }

    // Find the selected article from the articles list
    const selectedArticleObj = articlesList.find(a => a.value === selectedArticle);

    // console.log('Article selected:', selectedArticleObj);

    if (selectedArticleObj && selectedArticleObj.category) {
      // Get the category from the article's category field
      const articleCategory = selectedArticleObj.category;

      // Find the corresponding blog_id in the blogIdToCategoryMap
      let matchingBlogId = 'all';

      for (const [blogId, categoryName] of Object.entries(blogIdToCategoryMap)) {
        if (categoryName.toLowerCase() === articleCategory.toLowerCase()) {
          matchingBlogId = blogId;
          break;
        }
      }

      // console.log(`Auto-selecting category: ${articleCategory} (Blog ID: ${matchingBlogId})`);

      // Auto-select the category
      setSelectedCategory(matchingBlogId);
    }
  }, [selectedArticle, articlesList, blogIdToCategoryMap]);


  const defaultGridLayout = [
    { id: "totalBlogViews", colSpan: 2 },
    { id: "clicksToProducts", colSpan: 2 },
    { id: "topProductClicks", colSpan: 2 },
    { id: "avgSessionDuration", colSpan: 2 },
    { id: "customerSupport", colSpan: 4 },
    { id: "performanceInsights", colSpan: 4 },
    { id: "contentOptimization", colSpan: 4 },
    { id: "strategicActions", colSpan: 4 },
    { id: "topSessions", colSpan: 4 },
    { id: "topEngagement", colSpan: 4 },
    { id: "topProducts", colSpan: 4 },
    { id: "chartovertime", colSpan: 12 },
    { id: "topArticles", colSpan: 12 },
  ];

  const fetchInsights = useCallback(async () => {
    if (!shopOrigin) return;

    setInsightsLoading(true);
    try {
      const response = await axios.get(
        `/analytics/generate-insights/?shop=${shopOrigin}&days=30`
      );

      // Ensure the response has the expected structure
      setInsights({
        performance_insights: response.data.performance_insights || [],
        content_optimization: response.data.content_optimization || [],
        strategic_actions: response.data.strategic_actions || [],
      });
    } catch (error) {
      console.error("Error fetching insights:", error);
      setInsights({
        performance_insights: [],
        content_optimization: [],
        strategic_actions: [],
      });
    } finally {
      setInsightsLoading(false);
    }
  }, [shopOrigin]);

  useEffect(() => {
    if (!shopOrigin) return;
    fetchInsights();
  }, [shopOrigin]);

  const calculateRowLayout = (items) => {
    const GRID_COLUMNS = 12;
    const rowLayouts = [];
    let currentRow = [];
    let currentRowSpan = 0;

    items.forEach((item, index) => {
      const nextSpan = currentRowSpan + item.colSpan;

      if (nextSpan <= GRID_COLUMNS) {
        currentRow.push({ ...item, adjustedColSpan: item.colSpan });
        currentRowSpan = nextSpan;
      } else {
        const remainingSpace = GRID_COLUMNS - currentRowSpan;

        if (remainingSpace >= 4 && item.colSpan > remainingSpace) {
          currentRow.push({ ...item, adjustedColSpan: remainingSpace });
          rowLayouts.push(currentRow);
          currentRow = [];
          currentRowSpan = 0;
        } else {
          if (currentRow.length > 0 && remainingSpace > 0) {
            const lastItem = currentRow[currentRow.length - 1];
            lastItem.adjustedColSpan =
              (lastItem.adjustedColSpan || lastItem.colSpan) + remainingSpace;
          }

          rowLayouts.push(currentRow);
          currentRow = [{ ...item, adjustedColSpan: item.colSpan }];
          currentRowSpan = item.colSpan;
        }
      }
      if (index === items.length - 1 && currentRow.length > 0) {
        const remainingSpace = GRID_COLUMNS - currentRowSpan;
        if (remainingSpace > 0 && currentRow.length > 0) {
          const lastItem = currentRow[currentRow.length - 1];
          lastItem.adjustedColSpan =
            (lastItem.adjustedColSpan || lastItem.colSpan) + remainingSpace;
        }
        rowLayouts.push(currentRow);
      }
    });
    return rowLayouts.flat();
  };

  const customCollisionDetection = (args) => {
    const closestCornersCollisions = closestCorners(args);
    if (closestCornersCollisions.length > 0) {
      return closestCornersCollisions;
    }
    return closestCenter(args);
  };

  // Update state initialization
  const [editMode, setEditMode] = useState(false);
  const [draggingHeight, setDraggingHeight] = useState({});
  const [gridLayout, setGridLayout] = useState([]);
  const [layoutLoading, setLayoutLoading] = useState(true);
  const [isSavingLayout, setIsSavingLayout] = useState(false);

  const [reviewTrigger, setReviewTrigger] = useState(null);
  const triggerReview = useReviewTrigger(shopOrigin);


  // Load layout from DB on mount
  useEffect(() => {
    if (!shopDomain) return;

    const loadLayout = async () => {
      setLayoutLoading(true);
      try {
        const layout = await loadLayoutFromDB(shopDomain);
        setGridLayout(calculateRowLayout(layout));
      } catch (error) {
        console.error('Failed to load layout:', error);
        setGridLayout(calculateRowLayout(defaultGridLayout));
      } finally {
        setLayoutLoading(false);
      }
    };

    loadLayout();
  }, [shopDomain]);



  // Auto-save layout when it changes (debounced)
  useEffect(() => {
    if (!shopDomain || layoutLoading || gridLayout.length === 0) return;

    // Debounce save to avoid too many API calls
    const timeoutId = setTimeout(async () => {
      setIsSavingLayout(true);
      await saveLayoutToDB(shopDomain, gridLayout);
      setIsSavingLayout(false);
    }, 1000); // Save 1 second after last change

    return () => clearTimeout(timeoutId);
  }, [gridLayout, shopDomain, layoutLoading]);

  // useEffect(() => {
  //   saveOrder("gridLayout", gridLayout);
  // }, [gridLayout]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(MouseSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor)
  );

  // Update drag end handler
  function handleDragEnd({ active, over }) {
    if (!over || active.id === over.id) return;

    setGridLayout((items) => {
      const oldIndex = items.findIndex((item) => item.id === active.id);
      const newIndex = items.findIndex((item) => item.id === over.id);

      if (oldIndex !== -1 && newIndex !== -1) {
        const newItems = [...items];
        newItems[oldIndex].colSpan = newItems[newIndex].colSpan;
        const reordered = arrayMove(newItems, oldIndex, newIndex);

        const preservedSpans = reordered.map((item) => ({
          ...item,
          colSpan: item.colSpan || item.adjustedColSpan || 4,
        }));

        return calculateRowLayout(preservedSpans);
      }
      return items;
    });

    setDraggingHeight((prev) => {
      const newPrev = { ...prev };
      delete newPrev[active.id];
      return newPrev;
    });
  }


  // Update reset handler
  const handleResetLayout = async () => {
    if (!shopDomain) return;

    setLayoutLoading(true);
    const success = await resetLayoutInDB(shopDomain);

    if (success) {
      setGridLayout(calculateRowLayout(defaultGridLayout));
    }

    setLayoutLoading(false);
    setEditMode(false);
  };


  // Add API functions
  const loadLayoutFromDB = async (shopDomain) => {
    try {
      const response = await axios.get(`/analytics/get-layout?shop=${shopDomain}`);
      if (response.data.status === 'success' && response.data.layout.length > 0) {
        return response.data.layout;
      }
      return defaultGridLayout;
    } catch (error) {
      console.error('Error loading layout from DB:', error);
      return defaultGridLayout;
    }
  };


  const saveLayoutToDB = async (shopDomain, layout) => {
    try {
      const response = await axios.post(
        `/analytics/save-layout?shop=${shopDomain}`,
        { layout },
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );
      return response.data.status === 'success';
    } catch (error) {
      console.error('Error saving layout to DB:', error);
      return false;
    }
  };

  const resetLayoutInDB = async (shopDomain) => {
    try {
      const response = await axios.post(`/analytics/reset-layout?shop=${shopDomain}`);
      return response.data.status === 'success';
    } catch (error) {
      console.error('Error resetting layout in DB:', error);
      return false;
    }
  };

  // Add this function before renderCard
  const createHeaderWithTooltip_top5article = (label, tooltipContent) => {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          gap: "4px",
          marginRight: "-6px",
        }}
      >
        <span style={{ fontWeight: 600, fontSize: "13px" }}>{label}</span>
        <ToolTip content={tooltipContent} dismissOnMouseOut>
          <Icon source={InfoIcon} tone="base" />
        </ToolTip>
      </div>
    );
  };

  const renderCard = (cardId) => {
    const renderMetricCard = (metric, index) => {
      if (!metric) {
        return (
          <Card>
            <div
              style={{
                minHeight: "120px",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                gap: "12px",
              }}
            >
              <Spinner size="small" />
              <Text as="p" variant="bodySm" tone="subdued">
                Loading data…
              </Text>
            </div>
          </Card>
        );
      }

      // Define tooltip content for each card
      const tooltipContent = {
        'Total Blog Views': 'Total number of views across all blog articles.',
        'Clicks to Products': 'Total product clicks generated directly from blog articles.',
        'Top Product Clicks': 'Most clicked product originating from blog traffic.',
        'Avg Session Duration': 'Average time visitors spend on your blog sessions.'
      };

      return (
        <Card>
          <BlockStack gap="300">
            <InlineStack align="space-between" blockAlign="center">
              <div
                style={{ display: "flex", alignItems: "center", gap: "2px" }}
              >
                <Text as="p" variant="bodySm" tone="subdued">
                  {metric.title}
                </Text>
                <ToolTip content={tooltipContent[metric.title]} dismissOnMouseOut>
                  <Icon source={InfoIcon} tone="base" />
                </ToolTip>
              </div>
              <div>
                <span
                  style={{
                    display: "inline-block",
                    width: "8px",
                    height: "8px",
                    borderRadius: "50%",
                    backgroundColor: metric.dotColor,
                  }}
                />
              </div>
            </InlineStack>

            {/* Dynamic variant based on card index */}
            <Text
              as="h2"
              variant={index === 2 ? "headingMd" : "heading3xl"}
              fontWeight="bold"
            >
              {metric.value}
            </Text>

            {/* Subvalue for "Top Product Clicks" card */}
            {metric.subvalue && (
              <Text variant="headingXl" as="h3" fontWeight="semibold">
                {metric.subvalue}
              </Text>
            )}

            <InlineStack gap="100" blockAlign="center">
              <Text
                as="span"
                tone={
                  metric.trend === "up"
                    ? "success"
                    : metric.trend === "down"
                      ? "critical"
                      : "subdued"
                }
                fontWeight="medium"
              >
                {metric.change}%{" "}
                {metric.trend === "up"
                  ? "↑"
                  : metric.trend === "down"
                    ? "↓"
                    : ""}
              </Text>
              <Text as="span" variant="bodySm" tone="subdued">
                {metric.subtext}
              </Text>
            </InlineStack>
          </BlockStack>
        </Card>
      );
    };

    const renderInsightsSectionHeader = (emoji, title, tooltipText) => {
      return (
        <InlineStack gap="150" blockAlign="center">
          <Text as="h3" variant="headingMd">
            {emoji} {title}
          </Text>
          <ToolTip content={tooltipText} dismissOnMouseOut>
            <Icon source={InfoIcon} tone="base" />
          </ToolTip>
        </InlineStack>
      );
    };

    const renderTableSectionHeader = (emoji, title, tooltipText) => {
      return (
        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
          <Text as="h3" variant="headingMd">
            {emoji} {title}
          </Text>
          <ToolTip content={tooltipText} dismissOnMouseOut>
            <Icon source={InfoIcon} tone="base" />
          </ToolTip>
        </div>
      );
    };


    switch (cardId) {
      case "totalBlogViews":
        return renderMetricCard(metricsData?.[0], 0);
      case "clicksToProducts":
        return renderMetricCard(metricsData?.[1], 1);
      case "topProductClicks":
        return renderMetricCard(metricsData?.[2], 2);
      case "avgSessionDuration":
        return renderMetricCard(metricsData?.[3], 3);
      case "performanceInsights":
        return (
          <Card>
            <BlockStack gap="400">
              {renderInsightsSectionHeader(
                "🚀",
                "Performance Insights",
                "Key performance findings based on user behavior."
              )}
              {insightsLoading ? (
                <div
                  style={{
                    minHeight: "180px",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    gap: "12px",
                  }}
                >
                  <Spinner size="small" />
                  <Text as="p" variant="bodySm" tone="subdued">
                    Analyzing your blog data…
                  </Text>
                </div>
              ) : (
                <BlockStack gap="400">
                  {insights.performance_insights &&
                    insights.performance_insights.length > 0 ? (
                    insights.performance_insights.map((insight, index) => (
                      <Text
                        as="p"
                        variant="bodySm"
                        key={insight.id || `perf-${index}`}
                      >
                        {" "}
                        {insight.parts && Array.isArray(insight.parts)
                          ? insight.parts.map((part, pIndex) =>
                            typeof part === "string" ? (
                              part
                            ) : part.type === "link" ? (
                              <Link
                                key={pIndex}
                                className="Polaris-Text--root Polaris-Text--bodySm"
                                to={`/article/${part.articleId}?${new URLSearchParams({
                                  shop: encodeURIComponent(shopOrigin),
                                  since: formatDateToYearMonthDayDateString(globalDateRange.since),
                                  until: formatDateToYearMonthDayDateString(globalDateRange.until),
                                  ...(selectedArticle && selectedArticle !== 'all' && { articleid: selectedArticle }),
                                  ...(selectedCategory && selectedCategory !== 'all' && {
                                    category: blogIdToCategoryMap[selectedCategory]
                                  })
                                }).toString()}`}
                                style={{
                                  color: "#303030",
                                  textDecoration: "none",
                                  cursor: "pointer",
                                }}
                              >
                                {part.text}
                              </Link>
                            ) : (
                              <span key={pIndex}>{part.text || JSON.stringify(part)}</span>
                            )
                          )
                          : typeof insight === "string"
                            ? insight
                            : JSON.stringify(insight)}
                      </Text>
                    ))
                  ) : (
                    <Text as="p" variant="bodySm" tone="subdued">
                      No insights available yet. Keep creating content!
                    </Text>
                  )}
                </BlockStack>
              )}
            </BlockStack>
          </Card>
        );

      case "contentOptimization":
        return (
          <Card>
            <BlockStack gap="400">
              {renderInsightsSectionHeader(
                "✍️",
                "Content Optimization",
                "AI suggestions to improve your articles and engagement."
              )}
              {insightsLoading ? (
                <div
                  style={{
                    minHeight: "180px",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    gap: "12px",
                  }}
                >
                  <Spinner size="small" />
                  <Text as="p" variant="bodySm" tone="subdued">
                    Analyzing your blog data…
                  </Text>
                </div>
              ) : (
                <BlockStack gap="400">
                  {insights.content_optimization &&
                    insights.content_optimization.length > 0 ? (
                    insights.content_optimization.map((insight, index) => (
                      <Text
                        as="p"
                        variant="bodySm"
                        key={insight.id || `opt-${index}`}
                      >
                        {insight.parts && Array.isArray(insight.parts)
                          ? insight.parts.map((part, pIndex) =>
                            typeof part === "string" ? (
                              part
                            ) : part.type === "link" ? (
                              <Link
                                key={pIndex}
                                className="Polaris-Text--root Polaris-Text--bodySm"
                                to={`/article/${part.articleId}?${new URLSearchParams({
                                  shop: encodeURIComponent(shopOrigin),
                                  since: formatDateToYearMonthDayDateString(globalDateRange.since),
                                  until: formatDateToYearMonthDayDateString(globalDateRange.until),
                                  ...(selectedArticle && selectedArticle !== 'all' && { articleid: selectedArticle }),
                                  ...(selectedCategory && selectedCategory !== 'all' && {
                                    category: blogIdToCategoryMap[selectedCategory]
                                  })
                                }).toString()}`}
                                style={{
                                  color: "#303030",
                                  textDecoration: "none",
                                  cursor: "pointer",
                                }}
                              >
                                {part.text}
                              </Link>
                            ) : (
                              <span key={pIndex}>{part.text || JSON.stringify(part)}</span>
                            )
                          )
                          : typeof insight === "string"
                            ? insight
                            : JSON.stringify(insight)}
                      </Text>
                    ))
                  ) : (
                    <Text as="p" variant="bodySm" tone="subdued">
                      No insights available yet. Keep creating content!
                    </Text>
                  )}
                </BlockStack>
              )}
            </BlockStack>
          </Card>
        );

      case "strategicActions":
        return (
          <Card>
            <BlockStack gap="400">
              {renderInsightsSectionHeader(
                "✅",
                "Strategic & Conversion Actions",
                "Recommended actions to increase conversions and traffic."
              )}
              {insightsLoading ? (
                <div
                  style={{
                    minHeight: "180px",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    gap: "12px",
                  }}
                >
                  <Spinner size="small" />
                  <Text as="p" variant="bodySm" tone="subdued">
                    Analyzing your blog data…
                  </Text>
                </div>
              ) : (
                <BlockStack gap="400">
                  {insights.strategic_actions &&
                    insights.strategic_actions.length > 0 ? (
                    insights.strategic_actions.map((insight, index) => (
                      <Text
                        as="p"
                        variant="bodySm"
                        key={insight.id || `strat-${index}`}
                      >
                        {" "}
                        {insight.parts && Array.isArray(insight.parts)
                          ? insight.parts.map((part, pIndex) =>
                            typeof part === "string" ? (
                              part
                            ) : part.type === "link" ? (
                              <Link
                                key={pIndex}
                                className="Polaris-Text--root Polaris-Text--bodySm"
                                to={`/article/${part.articleId}?${new URLSearchParams({
                                  shop: encodeURIComponent(shopOrigin),
                                  since: formatDateToYearMonthDayDateString(globalDateRange.since),
                                  until: formatDateToYearMonthDayDateString(globalDateRange.until),
                                  ...(selectedArticle && selectedArticle !== 'all' && { articleid: selectedArticle }),
                                  ...(selectedCategory && selectedCategory !== 'all' && {
                                    category: blogIdToCategoryMap[selectedCategory]
                                  })
                                }).toString()}`}
                                style={{
                                  color: "#303030",
                                  textDecoration: "none",
                                  cursor: "pointer",
                                }}
                              >
                                {part.text}
                              </Link>
                            ) : (
                              <span key={pIndex}>{part.text || JSON.stringify(part)}</span>
                            )
                          )
                          : typeof insight === "string"
                            ? insight
                            : JSON.stringify(insight)}
                      </Text>
                    ))
                  ) : (
                    <Text as="p" variant="bodySm" tone="subdued">
                      No insights available yet. Keep creating content!
                    </Text>
                  )}
                </BlockStack>
              )}
            </BlockStack>
          </Card>
        );

      case "topSessions":
        return (
          <Card>
            <BlockStack gap="300">
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                {renderTableSectionHeader(
                  "📊",
                  `Top ${topPostsSessionsCount !== "All" ? topPostsSessionsCount : ""} Blog Posts by Sessions`,
                  "Articles with the highest number of visitor sessions during the selected period."
                )}
              </div>
              {sessionLoading ? (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    padding: "40px",
                  }}
                >
                  <Spinner size="large" />
                </div>
              ) : topPostsBySessions.length > 0 ? (
                <>
                  <DataTable
                    columnContentTypes={["text", "numeric", "text"]}
                    headings={[
                      <Text as="span" variant="bodySm" tone="subdued">
                        ARTICLE
                      </Text>,
                      <Text as="span" variant="bodySm" tone="subdued">
                        SESSIONS
                      </Text>,
                      <Text as="span" variant="bodySm" tone="subdued">
                        TREND
                      </Text>,
                    ]}
                    rows={topPostsBySessions}
                  />
                  <div
                    style={{
                      borderTop: "1px solid #E1E3E5",
                      padding: "16px 0 0",
                      marginTop: "19px",
                    }}
                  >
                    <InlineStack blockAlign="center" align="center" gap="200">
                      <Text as="span" tone="subdued">
                        Show posts:
                      </Text>
                      <Popover
                        active={topPostsPopoverActive}
                        activator={
                          <Button
                            onClick={() => setTopPostsPopoverActive(!topPostsPopoverActive)}
                            disclosure
                            size="slim"
                          >
                            {getTopPostsLabel(topPostsSessionsCount)}
                          </Button>
                        }
                        onClose={() => setTopPostsPopoverActive(false)}
                      >
                        <Popover.Pane>
                          <OptionList
                            options={[
                              { label: "Top 5", value: 5 },
                              { label: "Top 10", value: 10 },
                              { label: "All", value: "All" },
                            ]}
                            selected={[topPostsSessionsCount]}
                            onChange={(value) => {
                              const selectedValue = String(value[0]);
                              if (selectedValue === 'All') {
                                // Navigate to AllPostTable with filters
                                const params = new URLSearchParams({
                                  shop: shopOrigin,
                                  type: 'sessions',
                                  since: formatDateToYearMonthDayDateString(globalDateRange.since),
                                  until: formatDateToYearMonthDayDateString(globalDateRange.until),
                                  limit: 'All',
                                });

                                // Add article filter
                                // if (selectedArticle && selectedArticle !== 'all') {
                                //   params.append('articleId', selectedArticle);
                                // }

                                // Add category filter
                                // if (selectedCategory && selectedCategory !== 'all') {
                                //   const categoryName = blogIdToCategoryMap[selectedCategory];
                                //   if (categoryName) {
                                //     params.append('category', categoryName);
                                //   }
                                // }

                                navigate(`/all-posts?${params.toString()}`, {
                                  state: {
                                    dashboardFilters: {
                                      articleId: selectedArticle !== 'all' ? selectedArticle : null,
                                      category: selectedCategory !== 'all' ? blogIdToCategoryMap[selectedCategory] : null
                                    }
                                  }
                                });
                              } else {
                                setTopPostsSessionsCount(selectedValue);
                              }
                              setTopPostsPopoverActive(false);
                            }}
                          />
                        </Popover.Pane>
                      </Popover>
                    </InlineStack>
                  </div>
                </>
              ) : (
                <Text as="p" tone="subdued">
                  No session data available for the selected period.
                </Text>
              )}
            </BlockStack>
          </Card>
        );

      case "topEngagement":
        return (
          <Card>
            <BlockStack gap="300">
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                {renderTableSectionHeader(
                  "🔥",
                  `Top ${topEngagementCount !== "All" ? topEngagementCount : ""} Blog Posts by Engagement`,
                  "Articles with the highest engagement rates based on time spent and scroll depth."
                )}
              </div>
              {engagementLoading ? (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    padding: "40px",
                  }}
                >
                  <Spinner size="large" />
                </div>
              ) : topEngagementData.length > 0 ? (
                <>
                  <DataTable
                    columnContentTypes={["text", "numeric", "text"]}
                    headings={[
                      <Text as="span" variant="bodySm" tone="subdued">
                        ARTICLE
                      </Text>,
                      <Text as="span" variant="bodySm" tone="subdued">
                        ENGAGEMENT %
                      </Text>,
                      <Text as="span" variant="bodySm" tone="subdued">
                        TREND
                      </Text>,
                    ]}
                    rows={topEngagementData}
                  />
                  <div
                    style={{
                      borderTop: "1px solid #E1E3E5",
                      padding: "16px 0 0",
                      marginTop: "19px",
                    }}
                  >
                    <InlineStack blockAlign="center" align="center" gap="200">
                      <Text as="span" tone="subdued">
                        Show engagement:
                      </Text>
                      <Popover
                        active={topEngagementPopoverActive}
                        activator={
                          <Button
                            onClick={() => setTopEngagementPopoverActive(!topEngagementPopoverActive)}
                            disclosure
                            size="slim"
                          >
                            {getTopEngagementLabel(topEngagementCount)}
                          </Button>
                        }
                        onClose={() => setTopEngagementPopoverActive(false)}
                      >
                        <Popover.Pane>
                          <OptionList
                            options={[
                              { label: "Top 5", value: 5 },
                              { label: "Top 10", value: 10 },
                              { label: "All", value: "All" },
                            ]}
                            selected={[topEngagementCount]}
                            onChange={(value) => {
                              const selectedValue = String(value[0]);
                              if (selectedValue === 'All') {
                                const params = new URLSearchParams({
                                  shop: shopOrigin,
                                  type: 'engagement',
                                  since: formatDateToYearMonthDayDateString(globalDateRange.since),
                                  until: formatDateToYearMonthDayDateString(globalDateRange.until),
                                  limit: 'All',
                                });

                                // Add article filter
                                // if (selectedArticle && selectedArticle !== 'all') {
                                //   params.append('articleId', selectedArticle);
                                // }

                                // Add category filter
                                // if (selectedCategory && selectedCategory !== 'all') {
                                //   const categoryName = blogIdToCategoryMap[selectedCategory];
                                //   if (categoryName) {
                                //     params.append('category', categoryName);
                                //   }
                                // }

                                navigate(`/all-posts?${params.toString()}`, {
                                  state: {
                                    dashboardFilters: {
                                      articleId: selectedArticle !== 'all' ? selectedArticle : null,
                                      category: selectedCategory !== 'all' ? blogIdToCategoryMap[selectedCategory] : null
                                    }
                                  }
                                });
                              } else {
                                setTopEngagementCount(selectedValue);
                              }
                              setTopEngagementPopoverActive(false);
                            }}
                          />
                        </Popover.Pane>
                      </Popover>
                    </InlineStack>
                  </div>
                </>
              ) : (
                <Text as="p" tone="subdued">
                  No engagement data available for the selected period.
                </Text>
              )}
            </BlockStack>
          </Card>
        );

      case "topProducts":
        return (
          <Card>
            <BlockStack gap="300">
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                {renderTableSectionHeader(
                  "🛍️",
                  `Top ${topProductsCount !== "All" ? topProductsCount : ""} Clicked Products`,
                  "Products that received the most clicks from your blog articles."
                )}
              </div>
              {topProductsLoading ? (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    padding: "40px",
                  }}
                >
                  <Spinner size="large" />
                </div>
              ) : topProductsData.length > 0 ? (
                <>
                  <DataTable
                    columnContentTypes={["text", "numeric", "text"]}
                    headings={[
                      <Text as="span" variant="bodySm" tone="subdued">
                        PRODUCT
                      </Text>,
                      <Text as="span" variant="bodySm" tone="subdued">
                        CLICKS
                      </Text>,
                      <Text as="span" variant="bodySm" tone="subdued">
                        TREND
                      </Text>,
                    ]}
                    rows={topProductsData}
                  />
                  <div
                    style={{
                      borderTop: "1px solid #E1E3E5",
                      padding: "16px 0 0",
                      marginTop: "19px",
                    }}
                  >
                    <InlineStack blockAlign="center" align="center" gap="200">
                      <Text as="span" tone="subdued">
                        Show products:
                      </Text>
                      <Popover
                        active={topProductsPopoverActive}
                        activator={
                          <Button
                            onClick={() => setTopProductsPopoverActive(!topProductsPopoverActive)}
                            disclosure
                            size="slim"
                          >
                            {getTopProductsLabel(topProductsCount)}
                          </Button>
                        }
                        onClose={() => setTopProductsPopoverActive(false)}
                      >
                        <Popover.Pane>
                          <OptionList
                            options={[
                              { label: "Top 5", value: 5 },
                              { label: "Top 10", value: 10 },
                              { label: "All", value: "All" },
                            ]}
                            selected={[topProductsCount]}
                            onChange={(value) => {
                              const selectedValue = String(value[0]);
                              if (selectedValue === 'All') {
                                const params = new URLSearchParams({
                                  shop: shopOrigin,
                                  type: 'products',
                                  since: formatDateToYearMonthDayDateString(globalDateRange.since),
                                  until: formatDateToYearMonthDayDateString(globalDateRange.until),
                                  limit: 'All',
                                });

                                // Add article filter
                                if (selectedArticle && selectedArticle !== 'all') {
                                  params.append('articleId', selectedArticle);
                                }

                                // Add category filter
                                if (selectedCategory && selectedCategory !== 'all') {
                                  const categoryName = blogIdToCategoryMap[selectedCategory];
                                  if (categoryName) {
                                    params.append('category', categoryName);
                                  }
                                }

                                navigate(`/all-posts?${params.toString()}`);
                              } else {
                                setTopProductsCount(selectedValue);
                              }
                              setTopProductsPopoverActive(false);
                            }}
                          />
                        </Popover.Pane>
                      </Popover>
                    </InlineStack>
                  </div>
                </>
              ) : (
                <Text as="p" tone="subdued">
                  No product click data available for the selected period.
                </Text>
              )}
            </BlockStack>
          </Card>
        );

      case 'chartovertime':
        const currentChartData = chartData[activeTab.toLowerCase()];

        // Format dates
        const formatDateLabel = (dateString) => {
          const date = new Date(dateString);
          const options = { month: 'short', day: 'numeric' };
          return date.toLocaleDateString('en-US', options);
        };

        const chartCategories = currentChartData.map((item) => formatDateLabel(item.date));
        const chartSeriesData = currentChartData.map((item) => item.value);

        // Calculate marker interval based on screen size
        const getMarkerInterval = () => {
          const dataLength = chartCategories.length;

          if (smDown) {
            // Mobile: Show markers every 5 days for 30+ days
            if (dataLength >= 30) return 5;
            if (dataLength >= 14) return 3;
            return 1; // Show all for less than 14 days
          }

          // Desktop/Tablet: Show all markers
          return 1;
        };

        // Create discrete markers array for specific points only
        const markerInterval = getMarkerInterval();
        const discreteMarkers = [];

        currentChartData.forEach((item, index) => {
          if (index % markerInterval === 0) {
            discreteMarkers.push({
              seriesIndex: 0,
              dataPointIndex: index,
              fillColor: '#6366F1',
              strokeColor: '#fff',
              size: 5,
            });
          }
        });

        // Dynamic tick amount based on screen size
        const getTickAmount = () => {
          const dataLength = chartCategories.length;

          if (smDown) {
            // Mobile (< 680px): For 30 days, show ~6 labels (every 5 days)
            if (dataLength >= 30) return 6;
            if (dataLength >= 14) return 4;
            return Math.min(dataLength, 5);
          } else if (mdDown) {
            // Tablet: show moderate labels
            if (dataLength >= 30) return 10;
            return Math.min(dataLength, 10);
          }

          // Desktop: show more labels
          return dataLength > 30 ? 15 : dataLength;
        };

        const chartOptions = {
          chart: {
            type: 'line',
            height: 350,
            toolbar: { show: false },
            zoom: { enabled: false },
          },
          stroke: {
            curve: 'smooth',
            width: 2.5,
            colors: ['#6366F1'],
          },
          markers: {
            size: 0, // Hide all markers by default
            discrete: discreteMarkers, // Show only specific markers
            hover: {
              size: 6,
              sizeOffset: 2
            },
          },
          xaxis: {
            categories: chartCategories,
            tickAmount: getTickAmount(),
            labels: {
              style: {
                colors: '#6B7280',
                fontSize: smDown ? '9px' : '11px',
                fontWeight: 500
              },
              // ✅ CORRECTED ROTATION LOGIC
              rotate: -45,
              rotateAlways: false,
              hideOverlappingLabels: true,
              trim: false,
              maxHeight: 120,
            },
            axisBorder: { show: true, color: '#E5E7EB' },
            axisTicks: { show: true, color: '#E5E7EB' },
          },
          yaxis: {
            title: {
              text: activeTab === 'Engagement' ? 'Engagement Score (0–100)' : activeTab,
              style: { color: '#6B7280', fontSize: '13px', fontWeight: 500 }
            },
            labels: {
              style: { colors: '#6B7280', fontSize: '11px', fontWeight: 500 }
            },
            axisBorder: { show: true, color: '#E5E7EB' },
          },
          grid: {
            borderColor: '#F3F4F6',
            strokeDashArray: 0,
            xaxis: { lines: { show: false } },
            yaxis: { lines: { show: true } },
          },
          tooltip: {
            enabled: true,
            y: {
              formatter: (val) => val.toLocaleString()
            }
          }
        };

        const chartSeries = [{
          name: activeTab,
          data: chartSeriesData
        }];

        return (
          <Card>
            <BlockStack gap="300">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <Text as="h3" variant="headingMd">{activeTab} Over Time</Text>
                  {activeTab === 'Engagement' && (
                    <ToolTip
                      content="Engagement score measures user interaction quality including time on page, scroll depth, and clicks. Higher scores indicate more engaged readers."
                      dismissOnMouseOut
                    >
                      <Icon source={InfoIcon} tone="base" />
                    </ToolTip>
                  )}
                </div>
                <div style={{ display: 'flex', gap: '8px', backgroundColor: '#F3F4F6', padding: '2px', borderRadius: '10px' }}>
                  {['Views', 'Engagement'].map(tab => (
                    <button
                      key={tab}
                      onClick={() => setActiveTab(tab)}
                      style={{
                        background: activeTab === tab ? '#fff' : 'transparent',
                        color: activeTab === tab ? '#111928' : '#6B7280',
                        border: activeTab === tab ? '1.5px solid #E5E7EB' : 'none',
                        borderRadius: '8px',
                        fontWeight: 500,
                        padding: '6px 15px',
                        boxShadow: activeTab === tab ? '0 1px 2px rgba(16, 24, 40, 0.05)' : 'none',
                        cursor: 'pointer',
                        transition: 'background 0.2s, color 0.2s'
                      }}
                    >
                      {tab}
                    </button>
                  ))}
                </div>
              </div>

              {chartLoading ? (
                <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
                  <Spinner size="large" />
                </div>
              ) : currentChartData.length > 0 ? (
                <>
                  <div style={{ width: '100%', backgroundColor: '#fff', borderRadius: '8px', padding: '16px' }}>
                    <Chart
                      options={chartOptions}
                      series={chartSeries}
                      type="line"
                      height={350}
                    />
                  </div>
                  {activeTab === 'Engagement' && (
                    <div style={{
                      padding: '12px',
                      backgroundColor: '#F9FAFB',
                      borderRadius: '8px',
                      borderLeft: '3px solid #6366F1'
                    }}>
                      <Text as="p" variant="bodySm" tone="subdued">
                        <strong>Reading the chart:</strong> Peaks indicate days with higher user engagement. This could result from compelling content, effective CTAs, or increased time spent reading. Use these insights to identify what resonates with your audience.
                      </Text>
                    </div>
                  )}
                </>
              ) : (
                <Text as="p" tone="subdued">
                  No {activeTab.toLowerCase()} data available for the selected period.
                </Text>
              )}
            </BlockStack>
          </Card>
        );



      case 'topArticles':
        return (
          <Card>
            <BlockStack gap="400">
              <Text as="h3" variant="headingMd">Top Articles</Text>
              {topArticlesLoading ? (
                <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
                  <Spinner size="large" />
                </div>
              ) : topArticlesData.length > 0 ? (
                <div>
                  <DataTable
                    columnContentTypes={['text', 'numeric', 'numeric', 'numeric', 'numeric']}
                    headings={[
                      <Text as="span" variant="bodySm" tone="subdued">ARTICLE</Text>,
                      createHeaderWithTooltip_top5article('VIEWS', 'Total number of times this article was viewed during the selected period.'),
                      createHeaderWithTooltip_top5article('ENGAGEMENT', 'Calculated using average time spent and scroll depth per article.'),
                      createHeaderWithTooltip_top5article('CONVERSION %', 'Total product clicks generated from this article.'),
                    ]}
                    rows={topArticlesData}
                  />
                </div>
              ) : (
                <Text as="p" tone="subdued">No article data available for the selected period.</Text>
              )}
            </BlockStack>
          </Card>
        );

      case "customerSupport":
        return (
          <Card>
            <BlockStack gap="400">
              <Text as="h3" variant="headingMd">Customer Support</Text>
              <div className="dashboard-stat-content card3">
                <Text as="p">
                  Find quick answers in detailed tutorials and frequently asked
                  questions.
                </Text>
              </div>
              <div
                style={{
                  marginTop: "20px",
                  display: "flex",
                  flexDirection: "column",
                  gap: "15px",
                  justifyContent: "space-between",
                }}
              >
                <div>
                  <Button
                    variant="plain"
                    onClick={async () => {
                      const triggerFn = reviewTrigger || triggerReview;
                      try {
                        await triggerFn();
                      } catch (err) {
                        console.error("Error triggering review:", err);
                      }
                    }}
                  >
                    Share Feedback
                  </Button>
                </div>
                <div>
                  <Button variant="plain" onClick={openWidget}>
                    Talk to Us
                  </Button>
                </div>
                <div>
                  <Link
                    target="_blank"
                    to="https://bloghero.freshdesk.com/support/solutions/206000059988"
                  >
                    <Button variant="plain">FAQ</Button>
                  </Link>
                </div>
              </div>
            </BlockStack>
          </Card>
        );

      default:
        return null;
    }
  };

  const customStyles = `
    .dashboard-grid-container {
      position: relative;
      display: grid;
      grid-template-columns: repeat(12, 1fr);
      gap: 16px;
      margin: 24px 0;
    }

    .dashboard-grid-container > * {
      min-width: 0;
      width: 100%;
      transition: grid-column 200ms ease;
    }

    .sortable-card {
      transition: transform 200ms ease, opacity 200ms ease, box-shadow 200ms ease;
      will-change: transform;
    }

    .sortable-card[style*="translate"] {
      transition: none !important;
    }

    .sortable-card[data-dragging="true"] {
      transition: none !important;
      cursor: grabbing !important;
      overflow: hidden;
    }

    .sortable-card[data-dragging="true"]::after {
      content: "";
      position: absolute;
      inset: 0;
      border: 2px dashed #005bd3;
      border-radius: 8px;
      pointer-events: none;
      opacity: 0.4;
    }

    .sortable-card.is-edit {
      cursor: grab;
    }

    .sortable-card.is-edit > div {
      transition: transform 0.2s ease-out, box-shadow 0.2s ease-out;
    }

    .sortable-card.is-edit:hover > div {
      transform: translateY(-4px) scale(1.01);
      box-shadow: 0 8px 20px rgba(0, 91, 211, 0.15) !important;
    }

    .sortable-card.is-edit:active {
      cursor: grabbing !important;
    }

    .sortable-card.is-edit:active > div {
      transform: translateY(-2px) scale(1.005);
    }

    .sortable-card.is-edit * {
      pointer-events: none;
    }

    .sortable-card.is-edit {
      pointer-events: auto;
    }

    .Polaris-ShadowBevel {
      height: 100%;
    }

    .edit_dashboard_div {
      display: flex;
      justify-content: flex-end;
      gap: 10px;
      // margin-bottom: 20px;
      width: 100%
    }

    .dashboard-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 20px;
    }

    @media(max-width: 425px){
      .header-texts{
        margin-bottom: 15px;
      }
      .dashboard-header{
        display: flex;
        align-items: normal;
        flex-direction: column;
      }
    }

    .filters-wrapper {
      display: flex;
      padding: 1rem;
      flex-direction: row;
      align-items: center;
      justify-content: space-between;
    }

    .filters-header {
        margin-bottom: 1rem;
    }

    .filters-controls {
      display: flex;
      gap: 1rem;
    }

    .filter-item {
      display: flex;
      gap: 0.5rem;
      align-items: center;
      justify-content: space-between;
    }

    .filter-control {
        width: 100%;
    }
    
    .filter-control .Polaris-Button {
        justify-content: space-between;
    }

    @media(max-width: 1024px){
      .filters-wrapper{
        display: block;
      }
      .filter-item{
        display: flex;
        gap: 0.5rem;
        justify-content: space-between;
      }
      .filters-controls{
        display: flex;
        flex-direction: column;
        gap: 1rem;
      }
    }

    /* Tablet and Desktop */
    @media (max-width: 769px) {
        .filters-controls {
            align-items: normal;
        }

        .filter-item {
            grid-template-columns: auto 1fr;
            align-items: center;
        }

        .filter-label {
            white-space: nowrap;
        }
    }

    
    @media(max-width: 425px){
      .filter_text{
        display: none;
      }

      .filter_parent_div .filters-wrapper {
        padding: 1rem 0.75rem;
      }
      
      .filter_parent_div .filters-controls {
        display: flex;
        /* gap: 0.75rem; */
        flex-direction: row;
        flex-wrap: wrap;
      }
      
      .filter_parent_div .filter-item {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        gap: 0.5rem;
      }
      .filters-controls .filter-item > div:first-child {
        display: none;
      }
      
      .filter_parent_div .filter-item .Polaris-Button {
        width: 100%;
        justify-content: space-between;
      }
      
    }

    .Polaris-Select__Content {
      max-width: 160px;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .Polaris-Select__SelectedOption {
      max-width: 100%;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    /* DatePicker styles from attached file */
    .dp-with-header .Polaris-DatePicker__Title {
      display: none;
    }

    .dp-header {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
      padding: 8px 12px;
    }

    .dp-label {
      font-weight: 600;
    }

    .Polaris-ButtonGroup {
      margin-left: 0px;
      margin-top: 6px;
      border: 1px solid black;
      width: fit-content;
      padding: 1px;
      border-radius: 10px;
    }

    .Polaris-Button--pressed {
      background-color: #000 !important;
      color: #fff !important;
      border-color: #000 !important;
    }

    .Polaris-Button--pressed:hover,
    .Polaris-Button--pressed:focus,
    .Polaris-Button--pressed:active {
      background-color: #000 !important;
      color: #fff !important;
      border-color: #000 !important;
    }

    .css-le0vmg {
      width: 110%;
      transform: translate(-88px, 0px);
    }

    // filter dashboard css
    

    @media (max-width: 600px) {
      .css-le0vmg {
        width: 125%;
        transform: translate(-90px, 0px);
      }
    }

    @media (max-width: 490px) {
      .css-le0vmg {
        width: 130%;
        transform: translate(-90px, 0px);
      }
    }

    @media (max-width: 425px) {
      .css-le0vmg {
        width: 137%;
        transform: translate(-90px, 0px);
      }
    }

    @media (max-width: 375px) {
      .css-le0vmg {
        width: 144%;
        transform: translate(-90px, 0px);
      }
    }

    @media (max-width: 767px) {
      .css-le0vmg {
        width: 120%;
        transform: translate(-90px, 0px);
      }
    }

    @media (max-width: 320px) {
      .css-le0vmg {
        width: 157%;
        transform: translate(-100px, 0px);
      }
    }


    @media (max-width: 768px) {
      .dashboard-grid-container {
        display: flex !important;
        flex-direction: column !important;
        gap: 16px;
      }

      .sortable-card {
        width: 100% !important;
        grid-column: span 12 !important;
      }

      .filter-item .date-range-picker .Polaris-Button {
        min-width: 140px;
      }
    }
  `;

  return (
    <div style={{ padding: "0px 10px 0px 10px" }}>
      <Page fullWidth>
        <style>{customStyles}</style>

        <ReviewManager
          shopOrigin={shopOrigin}
          onTriggerAvailable={(fn) => setReviewTrigger(() => fn)}
        />

        <div className="dashboard-header">
          <div className="header-texts" style={{ width: "100%" }}>
            <Text className="" variant="heading2xl" as="h2" alignment="start">
              <span>AnalyticsHero</span>
            </Text>
            <Text alignment="start" variant="bodyLg" as="p" tone="subdued">
              Monitor your content performance and engagement
            </Text>
          </div>
          {/* // Update edit mode button to show save status */}
          <div className="edit_dashboard_div">
            <Button
              onClick={handleExportData}
              loading={isExporting}
              icon={ExportIcon}
              variant="primary"
            >
              Export Data
            </Button>
            <Button
              icon={EditIcon}
              onClick={() => setEditMode((v) => !v)}
              loading={isSavingLayout}
            >
              {editMode ? (isSavingLayout ? 'Saving...' : 'Done') : ''}
            </Button>
            {editMode && (
              <Button icon={ResetIcon} onClick={handleResetLayout}>
                Reset
              </Button>
            )}
          </div>
        </div>

        <div className="filter_parent_div">
          <Card>
            <div className="filters-wrapper">
              <div className="filter_text">
                <Text variant="headingMd" as="h3">
                  Filters
                </Text>
              </div>

              <div className="filters-controls">
                <div className="filter-item">
                  <div>
                    <Text as="span" tone="subdued">
                      Date Range:
                    </Text>
                  </div>
                  <div className="date-range-picker">
                    <DateRangePicker
                      initialDateRange={getInitialDateRange(dateRange)}
                      onChange={handleDateRangeChange}
                      disabled={isLoading}
                    />
                  </div>
                </div>

                {/* Category Popover */}
                <div className="filter-item">
                  <div>
                    <Text as="span" tone="subdued">
                      Category:
                    </Text>
                  </div>
                  <Popover
                    active={categoryPopoverActive}
                    activator={
                      <Button
                        onClick={() =>
                          setCategoryPopoverActive(!categoryPopoverActive)
                        }
                        disclosure
                        disabled={isLoading}
                      >
                        {getCategoryLabel(selectedCategory)}
                      </Button>
                    }
                    onClose={() => setCategoryPopoverActive(false)}
                  >
                    <Popover.Pane>
                      <Scrollable style={{ maxHeight: "300px" }}>
                        <OptionList
                          options={blogsList}
                          selected={[selectedCategory]}
                          onChange={(value) => {
                            handleCategoryChange(value[0]);
                            setCategoryPopoverActive(false);
                          }}
                        />
                      </Scrollable>
                    </Popover.Pane>
                  </Popover>
                </div>

                {/* Article Popover */}
                <div className="filter-item">
                  <div>
                    <Text as="span" tone="subdued">Article</Text>
                  </div>
                  <Popover
                    active={articlePopoverActive}
                    activator={
                      <Button
                        onClick={() => setArticlePopoverActive(!articlePopoverActive)}
                        disclosure
                        disabled={filteredArticlesList.length === 0 || isLoading}
                      >
                        <span
                          style={{
                            maxWidth: 240,
                            display: 'inline-block',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            verticalAlign: 'bottom',
                          }}
                        >
                          {getArticleLabel(selectedArticle)}
                        </span>
                      </Button>
                    }
                    onClose={() => setArticlePopoverActive(false)}
                  >
                    <Popover.Pane>
                      <Scrollable style={{ maxHeight: '300px' }}>
                        <OptionList
                          options={filteredArticlesList.length === 0 ?
                            [{ label: 'No articles in this category', value: 'none' }] :
                            filteredArticlesList
                          }
                          selected={[selectedArticle]}
                          onChange={(value) => {
                            if (value[0] !== 'none') {
                              handleArticleChange(value[0]);
                              setArticlePopoverActive(false);
                            }
                          }}
                        />
                      </Scrollable>
                    </Popover.Pane>
                  </Popover>
                </div>
                {/* Reset Button */}
                <div className="filter-item">
                  <Link
                    to="#"
                    onClick={(e) => {
                      e.preventDefault(); // Prevent navigation
                      // Reset all filters to default
                      setSelectedCategory('all');
                      setSelectedArticle('all');
                      setDateRange('30');
                      setCurrentDatePeriod(null);

                      // Reset to default date range (Last 30 days)
                      const today = new Date();
                      today.setHours(0, 0, 0, 0);
                      const defaultRange = {
                        since: new Date(new Date().setDate(today.getDate() - 30)),
                        until: today,
                      };
                      setGlobalDateRange(defaultRange);
                    }}
                    style={{
                      textDecoration: 'none',
                      display: 'inline-block',
                    }}
                  >
                    <Button
                      disabled={isLoading}
                      variant="plain"
                    >
                      Reset Filters
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          </Card>
        </div>

        {/* Error State */}
        {error && (
          <Card>
            <BlockStack gap="300">
              <Text as="p" variant="bodyMd" tone="critical">
                Error: {error}
              </Text>
              <Button onClick={fetchDashboardMetrics}>Retry</Button>
            </BlockStack>
          </Card>
        )}

        {/* Dashboard Grid — always rendered; individual cards show skeletons while data loads */}
        {!error && (
          <DndContext
            sensors={sensors}
            collisionDetection={customCollisionDetection}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={gridLayout.map((item) => item.id)}
              strategy={rectSortingStrategy}
            >
              <div className="dashboard-grid-container">
                {gridLayout.map((item) => (
                  <SortableCard
                    key={item.id}
                    id={item.id}
                    editMode={editMode}
                    draggingHeight={draggingHeight}
                    style={{
                      gridColumn: `span ${item.adjustedColSpan || item.colSpan
                        }`,
                    }}
                  >
                    {renderCard(item.id)}
                  </SortableCard>
                ))}
              </div>
            </SortableContext>
          </DndContext>
        )}
      </Page>
    </div>
  );
}

export default Dashboard;